import React, { useState, useEffect, useCallback } from 'react';
import { menuItemService, pageService } from '../../services/api';
import {
  GripVertical, Plus, Trash2, Save, X, ChevronRight, ChevronDown,
  ExternalLink, Eye, EyeOff, MoveVertical
} from 'lucide-react';

function NavigationBuilder() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [pages, setPages] = useState([]);
  const [pageSearch, setPageSearch] = useState('');
  const [form, setForm] = useState({
    label: '', url: '', icon: '', location: 'header', sort_order: 0,
    parent_id: null, is_active: true, open_new_tab: false,
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filterLocation, setFilterLocation] = useState('header');

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await menuItemService.index();
      const data = Array.isArray(res) ? res : res?.data || [];
      setItems(flattenMenuItems(data));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  useEffect(() => {
    const fetchPages = async () => {
      try {
        const data = await pageService.index({ per_page: 100, published: 1 });
        setPages(Array.isArray(data) ? data : data?.data || []);
      } catch (e) {
        console.error('Pages load error', e);
      }
    };
    fetchPages();
  }, []);

  useEffect(() => {
    setForm((prev) => ({ ...prev, location: filterLocation }));
  }, [filterLocation]);

  const flattenMenuItems = (list) => {
    const result = [];
    const walk = (arr) => {
      for (const item of arr || []) {
        result.push(item);
        if (item.children && item.children.length) walk(item.children);
      }
    };
    walk(list);
    return result;
  };

  const filteredItems = items.filter((i) => i.location === filterLocation);
  const rootItems = filteredItems.filter((i) => !i.parent_id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const getChildren = (parentId) => filteredItems.filter((i) => i.parent_id === parentId).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...form, parent_id: form.parent_id ? parseInt(form.parent_id) : null };
      if (editingId) await menuItemService.update(editingId, data);
      else await menuItemService.store(data);
      closeForm();
      loadItems();
    } catch (error) { alert('Error saving menu item'); }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ label: '', url: '', icon: '', location: filterLocation, sort_order: 0, parent_id: null, is_active: true, open_new_tab: false });
  };

  const startEdit = (item) => {
    setForm({ ...item, parent_id: item.parent_id || '' });
    setEditingId(item.id);
    setShowForm(true);
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this menu item?')) return;
    try { await menuItemService.destroy(id); loadItems(); } catch (e) { alert('Error deleting'); }
  };

  const toggleActive = async (item) => {
    try {
      await menuItemService.update(item.id, { is_active: !item.is_active });
      loadItems();
    } catch (e) {}
  };

  const moveItem = async (item, direction) => {
    const siblings = item.parent_id
      ? getChildren(item.parent_id)
      : rootItems;
    const idx = siblings.findIndex((s) => s.id === item.id);
    const target = idx + direction;
    if (target < 0 || target >= siblings.length) return;

    const arr = [...siblings];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    for (const [i, s] of arr.entries()) {
      try { await menuItemService.update(s.id, { sort_order: i + 1 }); } catch (e) {}
    }
    loadItems();
  };

  const onDragStart = (e, item) => { setDragId(item.id); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = async (e, targetItem, position) => {
    e.preventDefault();
    if (!dragId || dragId === targetItem.id) return;
    const dragged = items.find((i) => i.id === dragId);
    if (!dragged) return;

    const updates = { sort_order: targetItem.sort_order };
    if (position === 'inside') {
      updates.parent_id = targetItem.id;
      updates.sort_order = getChildren(targetItem.id).length + 1;
    } else if (position === 'before') {
      updates.parent_id = targetItem.parent_id;
    } else if (position === 'after') {
      updates.parent_id = targetItem.parent_id;
      updates.sort_order = targetItem.sort_order + 1;
    }

    try { await menuItemService.update(dragId, updates); } catch (e) {}
    loadItems();
    setDragId(null);
  };

  const TreeItem = ({ item, depth = 0 }) => {
    const children = getChildren(item.id);
    const isExpanded = expandedIds.has(item.id);
    const isDragging = dragId === item.id;

    return (
      <div className={isDragging ? 'opacity-40' : ''}>
        <div
          draggable
          onDragStart={(e) => onDragStart(e, item)}
          onDragOver={onDragOver}
          onDrop={(e) => onDrop(e, item, 'inside')}
          className="group flex items-center gap-2 px-3 py-2.5 rounded-lg border border-transparent hover:border-slate-700 hover:bg-slate-800/40 transition"
          style={{ paddingLeft: `${12 + depth * 24}px` }}
        >
          <GripVertical size={14} className="text-slate-600 cursor-grab shrink-0" />

          {children.length > 0 ? (
            <button onClick={() => toggleExpand(item.id)} className="shrink-0 text-slate-500 hover:text-slate-300">
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : (
            <span className="w-[14px] shrink-0" />
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-200 truncate">{item.label}</span>
              {item.open_new_tab && <ExternalLink size={10} className="text-slate-500 shrink-0" />}
              {!item.is_active && <span className="text-[10px] bg-red-600/10 text-red-400 px-1.5 py-0.5 rounded border border-red-600/20">Hidden</span>}
            </div>
            <div className="text-[10px] text-slate-500 truncate">{item.url}</div>
          </div>

          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
            <button onClick={() => moveItem(item, -1)} className="p-1 rounded hover:bg-slate-700 text-slate-500"><MoveVertical size={12} className="rotate-180" /></button>
            <button onClick={() => moveItem(item, 1)} className="p-1 rounded hover:bg-slate-700 text-slate-500"><MoveVertical size={12} /></button>
            <button onClick={() => toggleActive(item)} className={`p-1 rounded ${item.is_active ? 'text-red-400 hover:bg-red-600/10' : 'text-slate-500 hover:bg-slate-700'}`}>
              {item.is_active ? <Eye size={12} /> : <EyeOff size={12} />}
            </button>
            <button onClick={() => startEdit(item)} className="p-1 rounded hover:bg-slate-700 text-blue-400"><Save size={12} /></button>
            <button onClick={() => deleteItem(item.id)} className="p-1 rounded hover:bg-red-600/10 text-red-400"><Trash2 size={12} /></button>
          </div>
        </div>

        {isExpanded && children.map((child) => (
          <TreeItem key={child.id} item={child} depth={depth + 1} />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-100">Navigation Builder</h2>
          <p className="text-slate-400 text-sm mt-1">Drag items to reorder. Drop on another item to nest it. Click to edit.</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterLocation}
            onChange={(e) => setFilterLocation(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500"
          >
            <option value="header">Header</option>
            <option value="footer">Footer</option>
            <option value="sidebar">Sidebar</option>
          </select>
          <button
            onClick={() => { setShowForm(true); setEditingId(null); setForm((p) => ({ ...p, location: filterLocation })); }}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
          >
            <Plus size={16} /> Add Item
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4 max-w-2xl">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-200">{editingId ? 'Edit Menu Item' : 'New Menu Item'}</h3>
            <button type="button" onClick={closeForm} className="text-slate-500 hover:text-slate-300"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
            <input required placeholder="URL (e.g. /about)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select value={form.parent_id || ''} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500">
              <option value="">No Parent (Top Level)</option>
              {rootItems.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
            <input type="number" placeholder="Sort Order" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-slate-400">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="rounded" /> Active
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-400">
              <input type="checkbox" checked={form.open_new_tab} onChange={(e) => setForm({ ...form, open_new_tab: e.target.checked })} className="rounded" /> Open in New Tab
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition">{editingId ? 'Update' : 'Save'}</button>
            <button type="button" onClick={closeForm} className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium transition">Cancel</button>
          </div>
        </form>
      )}

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
        {loading ? (
          <div className="text-center py-12 text-slate-500 text-sm animate-pulse">Loading navigation...</div>
        ) : rootItems.length === 0 ? (
          <div className="text-center py-12">
            <MoveVertical size={32} className="mx-auto text-slate-600 mb-3 opacity-20" />
            <p className="text-slate-500 text-sm font-medium">No menu items for <strong className="text-red-500">{filterLocation}</strong>.</p>
            <p className="text-slate-600 text-xs mt-1">Click "Add Item" to start building your navigation.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {rootItems.map((item) => (
              <TreeItem key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-100">Quick add from CMS pages</h3>
            <p className="text-xs text-slate-500">Click a page to prefill the navigation form.</p>
          </div>
          <input
            value={pageSearch}
            onChange={(e) => setPageSearch(e.target.value)}
            placeholder="Search pages"
            className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-red-500"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {pages
            .filter((page) => page.title?.toLowerCase().includes(pageSearch.toLowerCase()))
            .slice(0, 6)
            .map((page) => (
              <button
                key={page.id}
                type="button"
                onClick={() => {
                  setForm({
                    label: page.title,
                    url: `/${page.slug}`,
                    icon: '',
                    location: filterLocation,
                    sort_order: (rootItems.length || 0) + 1,
                    parent_id: null,
                    is_active: true,
                    open_new_tab: false,
                  });
                  setEditingId(null);
                  setShowForm(true);
                }}
                className="text-left bg-slate-950 border border-slate-800 hover:border-red-400 rounded-xl px-4 py-3 text-sm text-slate-200 transition"
              >
                <div className="font-semibold text-slate-100">{page.title}</div>
                <div className="text-xs text-slate-500">/{page.slug}</div>
              </button>
            ))}
          {pages.length === 0 && <p className="text-sm text-slate-500">No CMS pages available.</p>}
        </div>
      </div>
    </div>
  );
}

export default NavigationBuilder;
